import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import './ListFilters.css';

// Search + filter controls shared by every list page. They sit inside the
// `to-toolbar` of a `to-panel` (see ListPager for the stylesheet note) and
// follow one rule: a control that is narrowing the list looks different from
// one that is not (primary border, tint, dot), and ClearFiltersButton /
// ActiveFilterChips reset it in one click.

const isTypingTarget = el =>
  el &&
  (el.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

// Search box. Press "/" anywhere on the page to focus it; Esc clears it.
export const ListSearch = ({
  value,
  onChange,
  placeholder = 'Search...',
  ariaLabel,
  shortcut = true,
}) => {
  const inputRef = useRef(null);
  const active = Boolean(value);

  useEffect(() => {
    if (!shortcut) return undefined;
    const onKey = e => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(document.activeElement)) return;
      const input = inputRef.current;
      // offsetParent is null for hidden tabs / collapsed panes.
      if (!input || input.offsetParent === null) return;
      e.preventDefault();
      input.focus();
      input.select();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [shortcut]);

  const clear = () => {
    onChange({ target: { value: '' } });
    inputRef.current?.focus();
  };

  return (
    <div className={`lf-search${active ? ' is-active' : ''}`}>
      <i className='ri-search-line lf-search-icon' aria-hidden='true' />
      <input
        ref={inputRef}
        type='text'
        className='form-control'
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={e => {
          if (e.key === 'Escape' && active) {
            e.preventDefault();
            clear();
          }
        }}
        aria-label={ariaLabel || placeholder}
      />
      {active ? (
        <button
          type='button'
          className='lf-search-clear'
          onClick={clear}
          aria-label='Clear search'
        >
          <i className='ri-close-line' aria-hidden='true' />
        </button>
      ) : (
        shortcut && (
          <kbd className='lf-search-hint' aria-hidden='true'>
            /
          </kbd>
        )
      )}
    </div>
  );
};

ListSearch.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
  shortcut: PropTypes.bool,
};

// Labelled <select>. `options` are { value, label }; the option whose value
// equals `emptyValue` ('' by default) is the "All ..." state and counts as
// inactive. Pass `allLabel` to have the "All" option rendered for you.
export const ListFilterSelect = ({
  label,
  value,
  onChange,
  options,
  allLabel,
  emptyValue = '',
  ariaLabel,
  disabled = false,
}) => {
  const active = String(value ?? '') !== String(emptyValue);
  return (
    <label className={`lf-select${active ? ' is-active' : ''}`}>
      <span className='lf-select-label'>
        {active && <span className='lf-dot' aria-hidden='true' />}
        {label}
      </span>
      <select
        className='form-select'
        value={value}
        onChange={e => onChange(e.target.value)}
        aria-label={ariaLabel || label}
        disabled={disabled}
      >
        {allLabel && <option value={emptyValue}>{allLabel}</option>}
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
};

ListFilterSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.node.isRequired,
    })
  ).isRequired,
  allLabel: PropTypes.string,
  emptyValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ariaLabel: PropTypes.string,
  disabled: PropTypes.bool,
};

// Always rendered so the toolbar does not jump; disabled while nothing is
// filtered, and shows how many filters are on when something is.
export const ClearFiltersButton = ({ count, onClick }) => (
  <button
    type='button'
    className={`lf-clear${count > 0 ? ' is-active' : ''}`}
    onClick={onClick}
    disabled={count === 0}
  >
    <i className='ri-filter-off-line' aria-hidden='true' />
    Clear filters
    {count > 0 && <span className='lf-clear-count'>{count}</span>}
  </button>
);

ClearFiltersButton.propTypes = {
  count: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
};

// Removable "Label: value" pills for every filter that is on.
// chips: [{ key, label, value, onRemove }]
export const ActiveFilterChips = ({ chips }) => {
  if (!chips.length) return null;
  return (
    <div className='lf-chips' role='group' aria-label='Active filters'>
      <span className='lf-chips-title'>Filtered by</span>
      {chips.map(chip => (
        <span key={chip.key} className='lf-chip'>
          <span className='lf-chip-label'>{chip.label}:</span>
          <span className='lf-chip-value' title={String(chip.value)}>
            {chip.value}
          </span>
          <button
            type='button'
            className='lf-chip-remove'
            onClick={chip.onRemove}
            aria-label={`Remove ${chip.label} filter`}
          >
            <i className='ri-close-line' aria-hidden='true' />
          </button>
        </span>
      ))}
    </div>
  );
};

ActiveFilterChips.propTypes = {
  chips: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      onRemove: PropTypes.func.isRequired,
    })
  ).isRequired,
};
